import type Assembly          from '@/domains/Assemblies/Assembly';
import type Shipment          from '@/domains/Shipments/Shipment';
import type Product           from '@/domains/Products/Product';
import type OrderPosition     from '@/domains/Orders/OrderPosition';
import Title_ru_RU_Class      from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU            from '@/core/misc/decorators/Title_ru_RU';
import serializeSimplified    from '@/core/misc/decorators/SerializeSimplified';
import TYPES                  from '@/core/services/Changelogs/types';
import BaseEntity             from '@/core/misc/BaseEntity';
import Utils                  from '@/core/utils/Utils';
import Core                   from '@/core/Core';
import Dictionary             from '@/core/types/Dictionary';
import Partner                from '@/domains/Partners/Partner';
import Store                  from '@/domains/Stores/Store';
import Person                 from '@/domains/Persons/Person';
import CargoUnit              from '@/domains/CargoUnits/CargoUnit';
import ValidationError        from '@/core/misc/ValidationError';
import CargoUnitsDiff         from '@/domains/TransportOrders/CargoUnitsDiff';
import Vehicle                from '@/domains/Vehicles/Vehicle';
import Driver                 from '@/domains/Drivers/Driver';
import TransportOrderPosition from '@/domains/TransportOrders/TransportOrderPosition';
import DateSimple             from '@/core/types/DateSimple';
import IntRange               from '@/core/types/IntRange';
import TransportDowntime      from '@/domains/TransportDowntime/TransportDowntime';
import SWPortalTask           from '@/domains/SWPortalTask/SWPortalTask';
import Company                from "@/domains/Companies/Company";
import Sum                    from "@/core/types/Sum";
import Volume                 from "@/core/types/Volume";
import Weight                 from "@/core/types/Weight";
import Qty                    from "@/core/types/Qty";


class TransportOrderType {
  id: string; name: string; toString() { return this.name }
  constructor({id, name}) {this.id = id; this.name = name;}
}

/** Таможенный статус ЗТК */
class TransportOrderBorderStatus {
  id: string; name: string; toString() { return this.name }
  constructor({id, name}) {this.id = id; this.name = name;}

  /** Гидратор статуса из { id, name } с бэка */
  static fromServer(data: any): TransportOrderBorderStatus {
    return new TransportOrderBorderStatus(data);
  }
}

@Title_ru_RU_Class('ЗТК')
class TransportOrder extends BaseEntity {
  /** Склад-перевалочный пункт (Электросталь) */
  private static readonly DEFAULT_DELIVERER_STORE_ID = 331;
  /** Юрлицо получателя по умолчанию для Сибирского здоровья */
  private static readonly DEFAULT_RECEIVER_COMPANY_ID = 66;
  /** Юрлицо получателя по умолчанию для Намангана */
  private static readonly NAMANGAN_RECEIVER_COMPANY_ID = 495;

  static Events = {
    Created: 'transportOrder:created',
    Updated: 'transportOrder:updated',
    Deleted: 'transportOrder:deleted',
  } as const;

  // ---- Тип ЗТК ----

  static Type = {
    SingleSender: 'single_sender',         // один отправитель, позиции — Assembly/Shipment
    Consolidated: 'consolidated',           // много отправителей, позиции — Assembly/Shipment/TransportOrder
    OrderPositionPickup: 'order_position_pickup',  // позиции — OrderPosition, едем забирать
    Return: 'return',                 // возврат товара
    Service: 'service',                // техника — краны, трактора, комбайны
    Internal: 'internal',               // перемещение между складами
  } as const;

  static types = Dictionary.fromArray([
    new TransportOrderType({ id: TransportOrder.Type.SingleSender,        name: 'Обычная' }),
    new TransportOrderType({ id: TransportOrder.Type.Consolidated,        name: 'Консолидированная' }),
    new TransportOrderType({ id: TransportOrder.Type.OrderPositionPickup, name: 'Забор у поставщика' }),
    new TransportOrderType({ id: TransportOrder.Type.Return,             name: 'Возврат поставщику' }),
    new TransportOrderType({ id: TransportOrder.Type.Service,            name: 'Аренда транспорта' }),
    new TransportOrderType({ id: TransportOrder.Type.Internal,           name: 'Внутреннее перемещение' }),
  ]);

  /**
   * MOCK: варианты таможенного статуса, пока их не отдаёт бэк.
   * Общий справочник для обоих сценариев: экспорт из УЗ и ввоз в УЗ из любой страны.
   */
  static borderStatuses = [
    new TransportOrderBorderStatus({ id: 'documents_ready',       name: 'Документы готовы' }),
    new TransportOrderBorderStatus({ id: 'declaration_filed',     name: 'ДТ подана' }),
    new TransportOrderBorderStatus({ id: 'release_uz',            name: 'Выпуск УЗ' }),
    new TransportOrderBorderStatus({ id: 'border',                name: 'Граница' }),
    new TransportOrderBorderStatus({ id: 'destination_clearance', name: 'Очистка в стране назначения' }),
    new TransportOrderBorderStatus({ id: 'cleared',               name: 'Растаможено' }),
    new TransportOrderBorderStatus({ id: 'sender_documents',      name: 'Документы отправителя' }),
    new TransportOrderBorderStatus({ id: 'sender_clearance',      name: 'Очистка отправителя' }),
    new TransportOrderBorderStatus({ id: 'import_declaration_uz', name: 'ДТ импорт УЗ' }),
    new TransportOrderBorderStatus({ id: 'release',               name: 'Выпуск' }),
  ];

  static REASON_UNAVAILABLE = {
    NO_EXIST_ENTITY_ON_BACKEND: 'Заявка ещё не создана',
    TRANSPORT_ORDER_ALREADY_HAS_PORTAL_TASK: 'Задача на портале уже создана',
    TRANSPORT_ORDER_FINISHED: 'ЗТК уже завершена',
    DRIVER_NOT_SELECTED: 'Водитель не выбран',
    NOT_SW_EMPLOYEE: 'Нет доступа',
    ONLY_ON_DEFAULT: 'Возможно только с обычными ЗТК',
  } as const;

  @Title_ru_RU('Тип ЗТК', TYPES.STRING)
  public type: string; // TransportOrder.Type.*

  // я рассматриваю это поле как защиту от дурака
  @Title_ru_RU('Подтверждение заявки', TYPES.BOOLEAN)
  public isConfirmed: boolean = false;

  @Title_ru_RU('Завершена', TYPES.BOOLEAN)
  public isFinished: boolean = false;

  @Title_ru_RU('Отправитель', TYPES.OBJECT)
  @serializeSimplified
  public sender: Partner;

  @serializeSimplified
  @Title_ru_RU('Компания отправителя', TYPES.OBJECT)
  public senderCompany: Company;

  @Title_ru_RU('Склад отправителя', TYPES.OBJECT)
  @serializeSimplified
  public senderStore: Store;

  @Title_ru_RU('Получатель', TYPES.OBJECT)
  @serializeSimplified
  public receiver: Partner;

  @Title_ru_RU('Компания получателя', TYPES.OBJECT)
  @serializeSimplified
  public receiverCompany: Company;

  @Title_ru_RU('Склад получателя', TYPES.OBJECT)
  @serializeSimplified
  public receiverStore: Store;

  @Title_ru_RU('Перевозчик', TYPES.OBJECT)
  @serializeSimplified
  public deliverer: Partner;

  @Title_ru_RU('Склад перевозчика', TYPES.OBJECT)
  @serializeSimplified
  public delivererStore: Store;

  /**
   * Водитель. Его обычные транспорт и госномер подставляются в пустые реквизиты ЗТК — см. fillVehicleFromDriver.
   * Данных о водителе ЗТК не отдаёт: карточка перечитывает его по id через drivers.get.
   * Уходит на бэк целиком: при создании ЗТК бэк сам создаёт или обновляет водителя
   */
  @Title_ru_RU('Водитель', TYPES.OBJECT)
  public driver: Driver | null = null;

  /** Транспортное средство — реквизит ЗТК, может быть известно без водителя */
  @Title_ru_RU('Транспортное средство', TYPES.OBJECT)
  @serializeSimplified
  public vehicle: Vehicle | null = null;

  @Title_ru_RU('Госномер', TYPES.STRING)
  public registrationNumber: string | null = null;

  /** Плановая дата забора груза */
  @Title_ru_RU('Плановая дата забора груза', TYPES.DATE)
  public pickupDatePlanned: DateSimple;

  /** Фактическая дата забора груза */
  @Title_ru_RU('Фактическая дата забора груза', TYPES.DATE)
  public pickupDateActual: DateSimple;

  /** Плановая дата прибытия груза */
  @Title_ru_RU('Плановая дата прибытия груза', TYPES.DATE)
  public arrivalDatePlanned: DateSimple;

  /** Фактическая дата прибытия груза */
  @Title_ru_RU('Фактическая дата прибытия груза', TYPES.DATE)
  public arrivalDateActual: DateSimple;

  @Title_ru_RU('Плановая стоимость перевозки', TYPES.NUMBER)
  public transportCostPlanned: Sum;

  @Title_ru_RU('Фактическая стоимость перевозки', TYPES.NUMBER)
  public transportCostActual: Sum;

  /** Заявленная стоимость груза */
  @Title_ru_RU('Объявленная сумма', TYPES.NUMBER)
  public declaredCost: Sum;

  public declaredCostCurrencyCode: string = Core.acl.currentPartnerCurrencyCode;

  @Title_ru_RU('Температурный режим', TYPES.INT_RANGE)
  public storeTemperatureRange: IntRange;

  // ---- ВГХ ----
  @Title_ru_RU('Объём', TYPES.NUMBER)
  public cargoVolume: Volume = Volume.zero();

  @Title_ru_RU('Вес брутто', TYPES.NUMBER)
  public cargoWeightGross: Weight = Weight.zero();

  @Title_ru_RU('Вес нетто', TYPES.NUMBER)
  public cargoWeightNet: Weight = Weight.zero();

  @Title_ru_RU('Количество паллетомест', TYPES.NUMBER)
  public cargoUnitsCount: Qty = Qty.zero();

  /**
   * Отгрузочные места: паллетоместа документов-позиций и корректировки ЗТК (items).
   * Группировка для отображения — cargoUnits.grouped
   */
  public cargoUnits: CargoUnitsDiff = new CargoUnitsDiff();

  @Title_ru_RU('Ответственный от получателя', TYPES.OBJECT)
  public responsibleReceiver: Person;


  public positions: TransportOrderPosition[] = [];


  @Title_ru_RU('Трек-номер', TYPES.STRING)
  public trackingNumber: string;

  @Title_ru_RU('Примечание', TYPES.STRING)
  public note: string;

  @Title_ru_RU('Комментарий', TYPES.STRING)
  public comment: string | null = null;

  /**
   * Таможенный статус — текущий этап таможенного оформления.
   * Меняется по ходу перевозки вместе с deadline.
   */
  @Title_ru_RU('Таможенный статус', TYPES.STRING)
  public borderStatus: TransportOrderBorderStatus;

  /**
   * Дедлайн — дата следующего действия логиста на любом этапе перевозки, не только таможенном.
   * Одна переменная на весь жизненный путь: на каждом этапе логист ставит сюда новую дату.
   */
  @Title_ru_RU('Дедлайн', TYPES.DATE)
  public deadline: DateSimple;

  /** Простои транспорта */
  public downtimes: TransportDowntime[] = [];

  /** Разрешаем прошедшие даты */
  // public isAvailableDateInThePast: boolean = false;

  // Только для type = 'single_sender'
  /** Номер и дата портальной задачи на обработку заявки */
  public swPortalTask: SWPortalTask;

  toString() {
    if (this.isExisting) {
      return `Заявка в ТК №${Core.utils.string.formatWithDelimiter(this.id)}`
    } else return 'Заявка в ТК'
  }

  get senderString(): string | null {
    if (!this.senderCompany) {
      return 'Не выбран';
    }
    if (this.senderCompany.name) {
      return `${this.senderCompany.name} (${this.sender.name})`;
    }
    return this.sender.name;
  }

  get receiverString(): string | null {
    if (!this.receiverCompany) {
      return 'Не выбран';
    }
    if (this.receiverCompany.name) {
      return `${this.receiverCompany.name} (${this.receiver.name})`;
    }
    return this.receiver.name;
  }

  /**
   * Поставщик зарубежный — страна юрлица отправителя не совпадает со страной из конфига.
   * Пока юрлицо или его страна не выбраны — считаем, что не зарубежный.
   */
  get isForeign(): boolean {
    const countryCode = this.senderCompany?.country?.code;

    if (Core.utils.isNullOrUndefined(countryCode) === false) {
      return countryCode !== Core.config.localCountry.code;
    }
    else {
      return false;
    }
  }

  get isSingleSender(): boolean {
    return this.type === TransportOrder.Type.SingleSender;
  }

  get isConsolidated(): boolean {
    return this.type === TransportOrder.Type.Consolidated;
  }

  get isOrderPositionPickup(): boolean {
    return this.type === TransportOrder.Type.OrderPositionPickup;
  }

  get isReturn(): boolean {
    return this.type === TransportOrder.Type.Return;
  }

  get isService(): boolean {
    return this.type === TransportOrder.Type.Service;
  }

  get isInternal(): boolean {
    return this.type === TransportOrder.Type.Internal;
  }

  /** если никто не редактировал зтк, то считаем её новой */
  get isNew(): boolean {
    return this.isConfirmed === false
      && this.isFinished === false
      && this.version === 1;
  }

  get isOfferBased(): boolean | undefined {
    // геттер задумывался только для создания задачи на портале
    // задача на портале может быть создана только из обычной заявки
    if (this.isSingleSender && this.positions.length > 0) {
      return (this.positions[0].document as Assembly).isOfferBased;
    } else {
      // использования этого геттера в каких-то других сценариях не предусмотрено
      return undefined;
    }
  }

  get typeString(): string {
    return TransportOrder.types.get(this.type)?.name ?? '--';
  }

  get hasDowntime(): boolean {
    return Core.utils.isNotEmptyArray(this.downtimes);
  }

  /**
   * Подставляет обычные транспорт и госномер водителя в незаполненные реквизиты ЗТК.
   * Уже введённое пользователем не перезаписывается
   */
  fillVehicleFromDriver(): void {
    if (Core.utils.isNullOrUndefined(this.driver) === false) {
      if (Core.utils.isNullOrUndefined(this.vehicle) === true) {
        this.vehicle = this.driver.vehicle ?? null;
      }
      else {
        // транспорт уже указан
      }

      if (Core.utils.isNullOrUndefined(this.registrationNumber) === true || this.registrationNumber === '') {
        this.registrationNumber = this.driver.registrationNumber ?? null;
      }
      else {
        // госномер уже указан
      }
    }
    else {
      // водителя нет — подставлять нечего
    }
  }

  get status(): string {
    if (this.isFinished) return 'Завершён'
    if (this.isConfirmed) return 'Утверждён'
    if (this.isConfirmed === false) return 'В работе'
    return '--';
  }

  static fromServer(data: any, result: TransportOrder = new TransportOrder()): TransportOrder {
    super.fromServer(data, result);
    // type приходит как строка с бэка
    result.type = data.type;
    result.isConfirmed = data.isConfirmed;
    result.isFinished = data.isFinished;

    result.sender = Core.utils.hydrate(data.sender, Partner.fromServer)!;
    result.senderCompany = Core.utils.hydrate(data.senderCompany, Company.fromServer)!;
    result.senderStore = Core.utils.hydrate(data.senderStore, Store.fromServer)!;

    result.receiver = Core.utils.hydrate(data.receiver, Partner.fromServer)!;
    result.receiverCompany = Core.utils.hydrate(data.receiverCompany, Company.fromServer)!;
    result.receiverStore = Core.utils.hydrate(data.receiverStore, Store.fromServer)!;

    result.deliverer = Core.utils.hydrate(data.deliverer, Partner.fromServer)!;
    result.delivererStore = Core.utils.hydrate(data.delivererStore, Store.fromServer)!;

    result.driver = Core.utils.hydrate(data.driver, Driver.fromServer);
    result.vehicle = Core.utils.hydrate(data.vehicle, Vehicle.fromServer);
    result.registrationNumber = data.registrationNumber ?? null;

    result.pickupDatePlanned = Core.utils.hydrate(data.pickupDatePlanned, DateSimple.fromServer)!;
    result.pickupDateActual = Core.utils.hydrate(data.pickupDateActual, DateSimple.fromServer)!;
    result.arrivalDatePlanned = Core.utils.hydrate(data.arrivalDatePlanned, DateSimple.fromServer)!;
    result.arrivalDateActual = Core.utils.hydrate(data.arrivalDateActual, DateSimple.fromServer)!;

    result.transportCostPlanned = Core.utils.hydrate(data.transportCostPlanned, Sum.fromServer)!;
    result.transportCostActual = Core.utils.hydrate(data.transportCostActual, Sum.fromServer)!;

    result.declaredCost = Core.utils.hydrate(data.declaredCost, Sum.fromServer)!;
    result.declaredCostCurrencyCode = data.declaredCostCurrencyCode ?? Core.acl.currentPartnerCurrencyCode;

    result.storeTemperatureRange = Core.utils.hydrate(data.storeTemperatureRange, IntRange.fromServer)!;

    result.cargoVolume      = Core.utils.hydrate(data.cargoVolume, Volume.fromServer) ?? Volume.zero();
    result.cargoUnitsCount  = Core.utils.hydrate(data.cargoUnitsCount, Qty.fromServer) ?? Qty.zero();
    result.cargoWeightGross = Core.utils.hydrate(data.cargoWeightGross, Weight.fromServer) ?? Weight.zero();
    result.cargoWeightNet   = Core.utils.hydrate(data.cargoWeightNet, Weight.fromServer) ?? Weight.zero();

    result.cargoUnits = new CargoUnitsDiff(Core.utils.hydrateArray(data.cargoUnits, CargoUnit.fromServer));

    // transportOrder.responsibleSender = Core.utils.hydrate(data.responsibleSender, Person.fromServer);
    result.responsibleReceiver = Core.utils.hydrate(data.responsibleReceiver, Person.fromServer)!;

    result.positions = Core.utils.hydrateArray(data.positions, TransportOrderPosition.fromServer);

    result.trackingNumber = data.trackingNumber;
    result.note = data.note;

    result.comment      = data.comment;
    result.borderStatus = Core.utils.hydrate(data.borderStatus, TransportOrderBorderStatus.fromServer)!;
    result.deadline     = Core.utils.hydrate(data.deadline, DateSimple.fromServer)!;

    result.downtimes = Core.utils.hydrateArray(data.downtimes, TransportDowntime.fromServer);

    result.swPortalTask = Core.utils.hydrate(data.swPortalTask, SWPortalTask.fromServer);

    return result;
  }

  /** Диспетчер создания ЗТК — вызывает конструктор соответствующий типу */
  // Разделение на типизированные конструкторы
  // Единый конструктор разросся — вложенные if/else по типам ЗТК
  // делали код нечитаемым. Разнесено по приватным конструкторам:
  // один тип = один метод. createFromType — диспетчер.
  static createFromType(typeId: string): TransportOrder {
    switch (typeId) {
      case TransportOrder.Type.SingleSender:        return TransportOrder._createSingleSender();
      case TransportOrder.Type.Consolidated:        return TransportOrder._createConsolidated();
      case TransportOrder.Type.OrderPositionPickup: return TransportOrder._createOrderPositionPickup();
      case TransportOrder.Type.Return:              return TransportOrder._createReturn();
      case TransportOrder.Type.Service:             return TransportOrder._createService();
      case TransportOrder.Type.Internal:            return TransportOrder._createInternal();
    }

    throw new TypeError(`TransportOrder.createFromType: неизвестный тип "${typeId}"`);
  }

  /** Обычная ЗТК — один отправитель, только сборки
   *  Отправитель заполняется из первой позиции через _initTransportOrderFromPosition */
  private static _createSingleSender(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.SingleSender;

    // ============= Получатель =============
    transportOrder.receiver = Core.sibWellPartner;
    transportOrder.responsibleReceiver = Core.currentUser;
    // Юрлицо и склад получателя подставляют источники селектов — см. find*-методы

    // ============= Отправитель =============
    // Отправитель — заполнится из первой добавленной позиции

    return transportOrder;
  }

  /** Консолидированная — много отправителей, рефрижератор, электросталь */
  private static _createConsolidated(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.Consolidated;

    // ============= Получатель =============
    transportOrder.receiver = Core.sibWellPartner;
    transportOrder.responsibleReceiver = Core.currentUser;
    // Юрлицо и склады подставляют источники селектов — см. find*-методы

    return transportOrder;
  }

  /** Забор у поставщика — едем забирать позиции заказа */
  private static _createOrderPositionPickup(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.OrderPositionPickup;

    // ============= Получатель =============
    transportOrder.receiver = Core.sibWellPartner;
    transportOrder.responsibleReceiver = Core.currentUser;
    // Юрлицо и склады подставляют источники селектов — см. find*-методы

    return transportOrder;
  }

  /** Возврат поставщику — мы отправитель, получатель заполнится из позиции */
  private static _createReturn(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.Return;

    // ============= Отправитель =============
    transportOrder.sender = Core.sibWellPartner;
    // transportOrder.responsibleSender = Core.currentUser;
    // Наш склад отправки подставляет источник селекта — см. findDefaultSenderStore
    // Склад получателя заполнится из позиции

    return transportOrder;
  }

  /** Аренда транспорта — без позиций с товаром */
  private static _createService(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.Service;

    // ============= Получатель =============
    transportOrder.receiver = Core.sibWellPartner;
    transportOrder.responsibleReceiver = Core.currentUser;
    // Юрлицо получателя подставляет источник селекта — см. findDefaultReceiverCompany

    return transportOrder;
  }

  /** Внутреннее перемещение между складами */
  private static _createInternal(): TransportOrder {
    const transportOrder = new TransportOrder();
    transportOrder.type = TransportOrder.Type.Internal;

    // ============= Получатель =============
    transportOrder.receiver = Core.sibWellPartner;
    transportOrder.responsibleReceiver = Core.currentUser;
    // Юрлицо и склад получателя подставляют источники селектов — см. find*-методы

    // ============= Отправитель =============
    transportOrder.sender   = Core.sibWellPartner;

    return transportOrder;
  }

  addPosition(position: TransportOrderPosition): void {
    this.positions.push(position);

    this._initTransportOrderFromPosition(position);
    this._addCargoUnitsFrom(position);
    this._calculateTemperatureRange();
    this.calculateDeclaredCost();
  }

  deletePosition(position: TransportOrderPosition): void {
    const reason = this.getReasonCannotDeletePosition();

    if (reason !== null) {
      throw new ValidationError(reason);
    }

    const index = this.positions.findIndex(item =>
      item.document.id === position.document.id
    );

    if (index === -1) {
      throw TypeError('TransportOrder.deletePosition: не удалось найти позицию для удаления');
    }

    this._deleteCargoUnitsFrom(this.positions[index]);
    this.positions.splice(index, 1);
    this._calculateTemperatureRange();
    this.calculateDeclaredCost();
  }

  addCargoUnit(cargoUnit: CargoUnit): void {
    if (!CargoUnit.isCargoUnit(cargoUnit)) {
      console.error('addCargoUnit: аргумент не является экземпляром CargoUnit');
      return;
    }

    this.cargoUnits.add(cargoUnit);
    this._calculateCargoSummary();
  }

  updateCargoUnit(updatedCargoUnit: CargoUnit, originalCompositeKey: string): void {
    if (!CargoUnit.isCargoUnit(updatedCargoUnit)) {
      console.error('updateCargoUnit: аргумент не является экземпляром CargoUnit');
      return;
    }

    this.cargoUnits.update(updatedCargoUnit, originalCompositeKey);
    this._calculateCargoSummary();
  }

  removeCargoUnit(cargoUnit: CargoUnit): void {
    this.cargoUnits.delete(cargoUnit);
    this._calculateCargoSummary();
  }

  /** Есть ли diff паллетомест относительно оригиналов — юнитов документов позиций */
  get hasCargoUnitsDiff(): boolean {
    const originals: CargoUnit[] = [];

    for (const position of this.positions) {
      position.cargoUnits?.forEach((cargoUnit: CargoUnit) => {
        originals.push(cargoUnit);
      });
    }

    return this.cargoUnits.containsDiff(originals);
  }

  /** Позицию удалить нельзя, пока есть корректировки паллетомест: они могли снимать её места */
  getReasonCannotDeletePosition(): string | null {
    if (this.hasCargoUnitsDiff === true) {
      return 'Нельзя удалить позицию: в ЗТК есть корректировки паллетомест';
    }
    else {
      return null;
    }
  }

  /**
   * Добавляет паллетоместа позиции как есть — с их id, без суммирования.
   * Корректировки ЗТК не трогаются: факт пересчитывается сам (каскад).
   */
  private _addCargoUnitsFrom(position: TransportOrderPosition): void {
    position.cargoUnits?.forEach((cargoUnit: CargoUnit) => {
      this.cargoUnits.add(cargoUnit);
    });

    this._calculateCargoSummary();
  }

  /** Убирает паллетоместа позиции по id (корректировки ЗТК остаются — см. CargoUnitsDiff.delete) */
  private _deleteCargoUnitsFrom(position: TransportOrderPosition): void {
    position.cargoUnits?.forEach((cargoUnit: CargoUnit) => {
      this.cargoUnits.delete(cargoUnit.id);
    });

    this._calculateCargoSummary();
  }

  private _calculateCargoSummary(): void {
    /** Агрегаты по местам через типовой plus со старта zero(). round10 не нужен —
     * каждый тип нормализован до своего scale конструктором (Volume — вверх). */
    this.cargoUnitsCount  = this.cargoUnits.items.reduce((sum, item) => sum.plus(item.qty), Qty.zero());
    this.cargoWeightGross = this.cargoUnits.items.reduce((sum, item) => sum.plus(item.totalWeightGross), Weight.zero());
    this.cargoWeightNet   = this.cargoUnits.items.reduce((sum, item) => sum.plus(item.totalWeightNet), Weight.zero());
    this.cargoVolume      = this.cargoUnits.items.reduce((sum, item) => sum.plus(item.totalVolume), Volume.zero());
  }

  private _calculateTemperatureRange(): void {
    if (this.positions.length === 0) {
      this.storeTemperatureRange = IntRange.fromObject({ start: undefined, end: undefined });
      return;
    }


    const intervals = this.positions
      .map(position => position.storeTemperatureRange)
      .filter(Boolean);

    if (intervals.length === 0) {
      return;
    }


    if (intervals.length === 1) {
      // Через фабрику, а не cloneDeep: копия должна остаться IntRange с методами (isEmpty и т.п.)
      this.storeTemperatureRange = IntRange.fromObject(intervals[0]);
    } else {
      this.storeTemperatureRange = IntRange.fromObject(Utils.number.numberIntervalIntersection(intervals));
    }
  }

  /**
   * Пересчитывает объявленную стоимость груза по всем позициям.
   * Вызывать после addPosition / deletePosition.
   *
   * Каждая позиция несёт declaredCost как Sum в своей валюте. Конвертируем
   * каждую в валюту ЗТК по курсу на дату создания и складываем в итого.
   */
  async calculateDeclaredCost(): Promise<void> {
    let total = Sum.zero();

    for (const position of this.positions) {
      const declaredCost = await position.getDeclaredCost();

      if (Core.utils.isNullOrUndefined(declaredCost)) {
        continue;
      }

      const converted = await Core.utils.money.convert(
        declaredCost,
        position.declaredCostCurrencyCode,
        this.declaredCostCurrencyCode,
        this.createdAt || new Date(),
      );

      total = total.plus(converted);
    }

    this.declaredCost = total;
  }

  /* ────────────────────────────────────────
     Отображение груза
     ──────────────────────────────────────── */

  getCargoUnitsStringRepresentation(): string {
    if (this.cargoUnits.grouped.size === 0) {
      return '--';
    }

    return `${this.cargoUnitsCount.formatAsInt()} ${Utils.string.pluralizeRu(this.cargoUnitsCount.toNumber(), 'место', 'места', 'мест')}, `
      + `объем: ${this.cargoVolume.format()} м3, `
      + `нетто: ${this.cargoWeightNet.format()} кг, `
      + `брутто: ${this.cargoWeightGross.format()} кг`;
  }

  /* ────────────────────────────────────────
     Валидация полей
     ──────────────────────────────────────── */

  validatePickupDatePlanned(value: any): string | null {
    if (this.id) {
      return null;
    }

    // Слава сказал убрать реквизит, Татьяна просила добавить свитчер для указания прошедшей даты
    // для заполнения документов задним числом, не знаю как лучше поступить, пока уберу валидацию
    return null;

    /** Если пользователь хочет указать даты задним числом, то
     * мы должны дать ему эту возможность */
    // if (this.isAvailableDateInThePast) {
    //     return null;
    // }
    //
    // const pickupDate = new DateSimple(value);

    /** Следующая плановая дата забора груза может быть только через 3 дня от дня создания,
     * то есть к текущий дате прибавить три дня и ты получишь нужное значение */
    // const edgeDate = new DateSimple().add({ days: 3 });

    // if (pickupDate.isBefore(edgeDate)) {
    //     return `Следующая возможная дата забора груза ${Utils.date.format(edgeDate)}`;
    // }

    // return null;
  }

  validateArrivalDatePlanned(value: any): string | null {
    if (this.id) {
      return null;
    }
    if (!this.pickupDatePlanned) {
      return null;
    }

    const arrivalDate = new DateSimple(value);

    if (arrivalDate.isBefore(this.pickupDatePlanned)) {
      return 'Нельзя указать раньше плановой даты забора груза';
    }

    return null;
  }

  validateArrivalDateActual(value: any): string | null {
    if (this.id) {
      return null;
    }
    if (!this.pickupDateActual) {
      return null;
    }
    const arrivalDate = new DateSimple(value);

    if (arrivalDate.isBefore(this.pickupDateActual)) {
      return 'Нельзя указать раньше даты забора груза';
    }

    return null;
  }

  private _initTransportOrderFromPosition(position: TransportOrderPosition): void {
    const doc = position.document as Assembly|Shipment|TransportOrder|Product|OrderPosition;
    if (!doc) { return; }

    // у консолидации много поставщиков, машина объезжает сразу многих, нет одного конкретного
    if (this.isConsolidated) return;
    // Аренда транспорта — позиция не несёт данных об участниках
    if (this.isService) return;
    // Внутреннее перемещение — оба участника мы, склады выбираются вручную
    if (this.isInternal) return;


    // Возврат — мы отправитель, из позиции заполняем получателя (поставщика)
    if (this.isReturn) {
      if (!this.receiver)            { this.receiver = position.sender; }
      if (!this.receiverStore)       { this.receiverStore = position.senderStore; }
      // if (!this.responsibleReceiver) { this.responsibleReceiver = position.responsibleSender; }
      return;
    }

    // Все остальные типы — из позиции заполняем отправителя
    if (!this.sender)            { this.sender = position.sender; }
    if (!this.senderCompany)            { this.senderCompany = position.senderCompany; }
    if (!this.senderStore)       { this.senderStore = position.senderStore; }
    // if (!this.responsibleSender) { this.responsibleSender = position.responsibleSender; }
  }

  /* ────────────────────────────────────────
     Валидация доступности действий
     ──────────────────────────────────────── */

  getReasonCannotDelete(): string | null {
    if (this.isExisting === false) {
      return TransportOrder.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    }

    return null;
  }

  /** Поля водителя правятся только у выбранного водителя */
  getReasonCannotEditDriver(): string | null {
    if (Core.utils.isNullOrUndefined(this.driver) === true) {
      return TransportOrder.REASON_UNAVAILABLE.DRIVER_NOT_SELECTED;
    }
    else {
      return null;
    }
  }

  getReasonCannotCreatePortalTask(): string | null {
    if (this.isExisting === false) {
      return TransportOrder.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    }

    if (Core.currentPartner?.isLSZ === false) {
      return TransportOrder.REASON_UNAVAILABLE.NOT_SW_EMPLOYEE;
    }

    if (this.swPortalTask !== null) {
      return TransportOrder.REASON_UNAVAILABLE.TRANSPORT_ORDER_ALREADY_HAS_PORTAL_TASK;
    }

    if (this.type !== TransportOrder.Type.SingleSender) {
      return TransportOrder.REASON_UNAVAILABLE.ONLY_ON_DEFAULT;
    }

    return null;
  }

  getReasonCannotSendNotification(): string | null {
    if (this.isExisting === false) {
      return TransportOrder.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;
    }

    if (this.isFinished) {
      return TransportOrder.REASON_UNAVAILABLE.TRANSPORT_ORDER_FINISHED;
    }

    if (this.type !== TransportOrder.Type.SingleSender) {
      return TransportOrder.REASON_UNAVAILABLE.ONLY_ON_DEFAULT;
    }

    return null;
  }

  getReasonCannotDownload(): string | null {
    if (this.type !== TransportOrder.Type.SingleSender) {
      return TransportOrder.REASON_UNAVAILABLE.ONLY_ON_DEFAULT;
    }

    return this.getReasonCannotDelete();
  }

  getReasonCannotShowFeed(): string | null {
    return this.getReasonCannotDelete();
  }

  /* ────────────────────────────────────────
     Дефолты реквизитов

     Раньше подставлялись прямо в фабриках, из вложенных полей партнёра
     (receiver.companies, sibWellPartner.stores). Значение попадало в модель
     до создания контрола, и VSelectV2 считал его осознанным выбором
     пользователя: смена юрлица склад не пересчитывала.

     Теперь это селекторы для .default() источников — выбирают из уже
     загруженного списка, а контрол помечает подставленное значение и
     пересчитывает его при смене родителя.
     ──────────────────────────────────────── */

  /** Дефолтное юрлицо получателя из загруженного списка. */
  findDefaultReceiverCompany(companies: Company[]): Company | undefined {
    if (this.receiver?.isLSZ === true) {
      /** ЛСЗ: юрлицо покупателя из документа первой позиции, а если его нет — дефолтное */
      let companyId = this.positions[0]?._document?.buyerCompany?.id;

      if (Core.utils.isNullOrUndefined(companyId) === true) {
        companyId = TransportOrder.DEFAULT_RECEIVER_COMPANY_ID;
      }

      return companies.find(company => company.id === companyId);
    }
    else if (this.receiver?.isNamangan === true) {
      /** Наманган: фиксированное юрлицо, а если его нет в списке — первое */
      const namanganCompany = companies.find(company => company.id === TransportOrder.NAMANGAN_RECEIVER_COMPANY_ID);

      if (namanganCompany !== undefined) {
        return namanganCompany;
      }
      else {
        return companies[0];
      }
    }
    else {
      /** Единственное юрлицо подставит сам контрол */
      return undefined;
    }
  }

  /** Дефолтный склад получателя из загруженного списка. */
  findDefaultReceiverStore(stores: Store[]): Store | undefined {
    if (this.isService === true) {
      /** Аренда транспорта — склад получателя не нужен */
      return undefined;
    } else {
      return stores.find(_store => _store.id === this.receiverCompany?.defaultStore?.id);
    }
  }

  /** Дефолтный склад отправителя из загруженного списка. */
  findDefaultSenderStore(stores: Store[]): Store | undefined {
    return stores.find(_store => _store.id === this.senderCompany?.defaultStore?.id);
  }

  /** Дефолтный склад перевозчика — перевалочный пункт. */
  findDefaultDelivererStore(stores: Store[]): Store | undefined {
    if (this.isConsolidated === false && this.isOrderPositionPickup === false) {
      /** Перевалочный пункт есть только у консолидации и забора */
      return undefined;
    } else if (Core.sibWellPartner.isLSZ === true) {
      return stores.find(_store => _store.id === TransportOrder.DEFAULT_DELIVERER_STORE_ID);
    } else {
      return undefined;
    }
  }

}

export default TransportOrder;
